/**
 * Inline text editing for HTML frames.
 *
 * The frame document is a live page: injected theme styles and scripts,
 * editing affordances and whatever the authored scripts generate all end up
 * in its DOM. Saving that DOM would persist all of it. Instead the app numbers
 * the elements of the authored HTML before rendering, the frame reports only
 * the new inner HTML of the elements the user changed, and the app applies
 * those edits to a fresh, inert parse of the authored HTML.
 */

const EDITABLE_TAGS = new Set([
  "P",
  "H1",
  "H2",
  "H3",
  "H4",
  "H5",
  "H6",
  "LI",
  "TD",
  "TH",
  "SPAN",
  "A",
]);
const BLOCK_TAGS = new Set([
  "P",
  "DIV",
  "SECTION",
  "ARTICLE",
  "HEADER",
  "FOOTER",
  "MAIN",
  "NAV",
  "ASIDE",
  "UL",
  "OL",
  "TABLE",
  "BLOCKQUOTE",
  "PRE",
  "H1",
  "H2",
  "H3",
  "H4",
  "H5",
  "H6",
  "LI",
  "TR",
  "TD",
  "TH",
  "FIGURE",
  "FIGCAPTION",
]);
/** Index of an authored body element, on editable elements and their content. */
const NODE_ATTRIBUTE = "data-denker-node";
const EDITABLE_ATTRIBUTE = "data-denker-editable";
/** Frame → app: `{ type, edit: HtmlFrameEdit }` when an edited element blurs. */
export const HTML_FRAME_EDIT_MESSAGE = "denker:html-edit";

export interface HtmlFrameEdit {
  /** `htmlEditBase` of the authored HTML the frame document was built from. */
  base: string;
  edits: { node: number; html: string }[];
}

function parseHtml(html: string): Document {
  return new DOMParser().parseFromString(html, "text/html");
}

/** Authored body elements in document order; the order is the node index. */
function bodyElements(doc: Document): Element[] {
  return Array.from(doc.body.querySelectorAll("*"));
}

function isEditable(element: Element): boolean {
  return (
    EDITABLE_TAGS.has(element.tagName) &&
    !Array.from(element.children).some((child) => BLOCK_TAGS.has(child.tagName))
  );
}

/** The outermost editable element is the editing host; nested ones are content. */
function isEditHost(element: Element): boolean {
  if (!isEditable(element)) return false;
  for (
    let parent = element.parentElement;
    parent;
    parent = parent.parentElement
  )
    if (isEditable(parent)) return false;
  return true;
}

function serializeHtml(doc: Document): string {
  const doctype = doc.doctype
    ? new XMLSerializer().serializeToString(doc.doctype)
    : "";
  return doctype + doc.documentElement.outerHTML;
}

/** Identifies one authored HTML string, so stale frame edits are dropped. */
export function htmlEditBase(html: string): string {
  let hash = 0x811c9dc5;
  for (let index = 0; index < html.length; index++) {
    hash ^= html.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return `${(hash >>> 0).toString(36)}-${html.length.toString(36)}`;
}

/** Marks editing hosts in the authored HTML, and everything inside them, by index. */
export function annotateEditableHtml(html: string): string {
  const doc = parseHtml(html);
  bodyElements(doc).forEach((element, index) => {
    const host = isEditHost(element);
    if (!host && !element.parentElement?.closest(`[${EDITABLE_ATTRIBUTE}]`))
      return;
    element.setAttribute(NODE_ATTRIBUTE, String(index));
    if (host) element.setAttribute(EDITABLE_ATTRIBUTE, "");
  });
  return serializeHtml(doc);
}

/**
 * Elements the frame reports keep their authored attributes, which drops
 * editing affordances and theme fixes; new elements typed by the user lose
 * only the editing markers.
 */
function restoreAuthoredAttributes(element: Element, authored: Element[]) {
  const node = element.getAttribute(NODE_ATTRIBUTE);
  const original = node !== null ? authored[Number(node)] : undefined;
  if (!original) {
    element.removeAttribute(NODE_ATTRIBUTE);
    element.removeAttribute(EDITABLE_ATTRIBUTE);
    element.removeAttribute("contenteditable");
    return;
  }
  for (const name of element.getAttributeNames()) element.removeAttribute(name);
  for (const { name, value } of Array.from(original.attributes))
    element.setAttribute(name, value);
}

/** The authored HTML with the frame's text edits applied. */
export function applyHtmlEdits(html: string, edit: HtmlFrameEdit): string {
  const doc = parseHtml(html);
  const authored = bodyElements(doc);
  let changed = false;
  for (const { node, html: innerHtml } of edit.edits) {
    const target = authored[node];
    if (!target?.isConnected || !isEditHost(target)) continue;
    const template = doc.createElement("template");
    template.innerHTML = innerHtml;
    // Charts the design runtime drew are redrawn from their attributes.
    template.content
      .querySelectorAll(`[data-chart-body]:not([${NODE_ATTRIBUTE}])`)
      .forEach((chart) => chart.remove());
    for (const element of Array.from(template.content.querySelectorAll("*")))
      restoreAuthoredAttributes(element, authored);
    const before = target.innerHTML;
    target.replaceChildren(template.content);
    changed ||= target.innerHTML !== before;
  }
  return changed ? serializeHtml(doc) : html;
}

/** Validates an edit report from the untrusted frame document. */
export function readHtmlFrameEdit(value: unknown): HtmlFrameEdit | null {
  if (!value || typeof value !== "object") return null;
  const { base, edits } = value as Record<string, unknown>;
  if (typeof base !== "string" || !Array.isArray(edits)) return null;
  const valid = edits.every(
    (entry) =>
      entry &&
      typeof entry === "object" &&
      Number.isInteger((entry as Record<string, unknown>).node) &&
      ((entry as Record<string, unknown>).node as number) >= 0 &&
      typeof (entry as Record<string, unknown>).html === "string",
  );
  return valid ? { base, edits: edits as HtmlFrameEdit["edits"] } : null;
}

/**
 * Frame runtime that makes the marked elements editable and reports changed
 * ones on blur (`HTML_FRAME_EDIT_MESSAGE`) or when the app flushes before
 * saving. Escape reverts the host to its content when it was focused.
 */
export function inlineEditScript(base: string): string {
  return `<script data-denker-runtime="inline-edit">
(function(){
  var base=${JSON.stringify(base)};
  var edits={},dirty=false;
  function record(el){
    var node=el.getAttribute("${NODE_ATTRIBUTE}");
    if(node===null)return;
    edits[node]=el.innerHTML;
    dirty=true;
  }
  function recordActiveChange(){
    var el=document.activeElement;
    if(!el||typeof el.__denkerText!=="string"||el.textContent===el.__denkerText)return;
    el.__denkerText=el.textContent;
    record(el);
  }
  function takeEdits(){
    recordActiveChange();
    if(!dirty)return null;
    var list=[];
    for(var node in edits)list.push({node:Number(node),html:edits[node]});
    edits={};dirty=false;
    return{base:base,edits:list};
  }
  window.__denkerTakeHtmlEdits=takeEdits;
  function setup(){
    document.querySelectorAll("[${EDITABLE_ATTRIBUTE}]").forEach(function(el){
      el.setAttribute("contenteditable","true");
      el.style.outline="none";
      el.style.cursor="text";
      el.__denkerText=el.textContent;
      el.addEventListener("focus",function(){el.__denkerText=el.textContent;el.__denkerFocusHtml=el.innerHTML;});
      el.addEventListener("blur",function(){
        el.__denkerFocusHtml=null;
        if(el.textContent===el.__denkerText)return;
        el.__denkerText=el.textContent;
        record(el);
        var edit=takeEdits();
        if(edit)window.parent.postMessage({type:"${HTML_FRAME_EDIT_MESSAGE}",edit:edit},"*");
      });
      el.addEventListener("keydown",function(e){
        if(e.key!=="Escape"||typeof el.__denkerFocusHtml!=="string")return;
        el.innerHTML=el.__denkerFocusHtml;
        el.__denkerText=el.textContent;
        el.blur();
      });
      el.addEventListener("paste",function(e){
        e.preventDefault();
        var text=e.clipboardData?e.clipboardData.getData("text/plain"):"";
        document.execCommand("insertText",false,text);
      });
    });
  }
  if(document.readyState==="loading")document.addEventListener("DOMContentLoaded",setup);
  else setup();
})();
</scr${""}ipt>`;
}
